<!--
  Return one record to the state the corpus delivered it in.

  This is the only control in the tool that **destroys human-drawn training data**, and it does
  it across every photograph of the stone at once — not just the one on screen. Everything about
  it is shaped by that.

  - **The figures come from the service, not from this session.** `api.resetPreview` is called
    when the confirmation opens, so what the annotator reads is true at the moment they read it.
    Adding up `session.boxes` would be wrong in the one direction that matters: somebody else may
    have boxed this stone since the page loaded, and a confirmation that understates what it
    destroys is worse than none at all.
  - **It names the people whose work goes.** Any writer may reset — the ruling that tasks are not
    gated rests on only trusted academics using this system — so what stands in for a permission
    check is knowing, in as many words, that nine of these twelve boxes are somebody else's.
  - **Two steps, and the second one is not the default.** The API refuses without `confirm=true`
    for the same reason; neither end infers the opt-in from the other.

  It sits in the record header, beside the `<h1>` naming what it resets, for the reason
  `InscriptionAttributes` sits there too: this is a fact about the *record*, and putting it near
  the box inspector would read as being about one photograph.
-->
<script lang="ts">
  import { api } from '../lib/api/client';
  import { asProblem, type ApiProblem } from '../lib/api/problem';
  import type { InscriptionResetSummary } from '../lib/api/types';
  import { directionLabel } from '../lib/draft';
  import { annotator } from '../lib/stores/annotator.svelte';
  import { queue } from '../lib/stores/queue.svelte';
  import { session } from '../lib/stores/session.svelte';

  let open = $state(false);
  let preview = $state<InscriptionResetSummary | null>(null);
  let loading = $state(false);
  let resetting = $state(false);
  let problem = $state<ApiProblem | null>(null);
  let done = $state<InscriptionResetSummary | null>(null);
  let reason = $state('');

  const record = $derived(session.inscription);
  const disabled = $derived(session.readOnly || !record || annotator.readOnlyAccount);

  /** Boxes on this record that somebody other than the signed-in annotator drew. */
  const othersBoxes = $derived(
    (preview?.contributors ?? [])
      .filter((item) => item.annotator_id !== annotator.id)
      .reduce((total, item) => total + item.boxes, 0),
  );

  function describe(item: { annotator_id: string; name: string | null; boxes: number }): string {
    const who = item.name || item.annotator_id;
    const mine = item.annotator_id === annotator.id ? ' (you)' : '';
    return `${who}${mine} — ${item.boxes}`;
  }

  async function start(): Promise<void> {
    if (!record) return;
    open = true;
    done = null;
    problem = null;
    loading = true;
    try {
      // Read now, not when the panel was rendered: this is the number the annotator is about
      // to act on.
      preview = await api.resetPreview(record.id);
    } catch (error) {
      problem = asProblem(error);
      preview = null;
    } finally {
      loading = false;
    }
  }

  function cancel(): void {
    open = false;
    preview = null;
    problem = null;
    reason = '';
  }

  async function confirm(): Promise<void> {
    if (!record || resetting) return;
    resetting = true;
    problem = null;
    try {
      done = await session.resetRecord(reason.trim() || undefined);
      open = false;
      reason = '';
      // The task meters counted this record's work; they no longer do.
      void queue.load();
    } catch (error) {
      problem = asProblem(error);
    } finally {
      resetting = false;
    }
  }
</script>

<button
  class="subtle small reset-open"
  {disabled}
  onclick={() => (open ? cancel() : void start())}
  aria-expanded={open}
>
  Reset…
</button>

{#if done}
  <div class="banner small col reset-note" role="status">
    <strong>Record reset.</strong>
    <p>
      {done.annotations} box{done.annotations === 1 ? '' : 'es'} and {done.absences} not-present
      mark{done.absences === 1 ? '' : 's'} cleared across {done.image_ids.length} image{done
        .image_ids.length === 1
        ? ''
        : 's'}, along with the reading direction and layout. The transliteration is untouched:
      it is the corpus's, not data entry.
    </p>
    <p class="muted">
      Every image went back to <em>unannotated</em>. This one is already
      <em>in progress</em> again — you still have it open, and opening an image claims it.
    </p>
    <div class="row">
      <button class="subtle small" onclick={() => (done = null)}>Dismiss</button>
    </div>
  </div>
{/if}

{#if open}
  <div class="banner warn small col reset-confirm">
    {#if loading}
      <p>Reading what is stored for this record…</p>
    {:else if problem}
      <p><strong>Could not read the record.</strong> {problem.message}</p>
      <p class="muted mono">code: {problem.code}</p>
      <div class="row">
        <button class="subtle small" onclick={cancel}>Close</button>
      </div>
    {:else if preview?.empty}
      <p>
        <strong>Nothing to reset.</strong> This record is already as the corpus delivered it — no
        boxes, no marks, no reading direction.
      </p>
      <div class="row">
        <button class="subtle small" onclick={cancel}>Close</button>
      </div>
    {:else if preview}
      <strong>Reset {preview.siglum_raw}?</strong>
      <ul class="what">
        <li>
          <strong>{preview.annotations}</strong>
          box{preview.annotations === 1 ? '' : 'es'} across
          <strong>{preview.image_ids.length}</strong>
          image{preview.image_ids.length === 1 ? '' : 's'} — every photograph of this stone, not
          only the one on screen
        </li>
        {#if preview.absences > 0}
          <li><strong>{preview.absences}</strong> letter(s) recorded as not present</li>
        {/if}
        {#if preview.statuses_reset.length > 0}
          <li>
            {preview.statuses_reset.length} image status(es) back to <em>unannotated</em>
          </li>
        {/if}
        <!--
          Called out rather than folded into the line above. A "done" or "in progress" status
          is progress, and redoing the work restores it; "does not show this record" is a
          finding somebody reached by looking, and the reset hands that photograph back to the
          queue as unexamined. Counting it as one of N statuses understates what goes.
        -->
        {#if preview.not_pictured_reset.length > 0}
          <li class="warn-text">
            <strong>{preview.not_pictured_reset.length}</strong>
            of those recorded that the photograph does not show this inscription — that finding
            is lost, and those images return to the queue as unexamined
          </li>
        {/if}
        {#if preview.reading_direction || preview.layout_type}
          <li>
            reading direction
            <strong>{directionLabel(preview.reading_direction)}</strong>
            and layout <strong>{preview.layout_type ?? 'not set'}</strong>
          </li>
        {/if}
      </ul>

      {#if preview.contributors.length > 0}
        <p class:warn-text={othersBoxes > 0}>
          Drawn by {preview.contributors.map(describe).join(', ')}.
          {#if othersBoxes > 0}
            <strong>{othersBoxes}</strong> of these {othersBoxes === 1 ? 'is' : 'are'} not yours.
          {/if}
        </p>
      {/if}

      <p class="muted">
        <strong>This cannot be undone.</strong> The transliteration, its tokens and the record's
        split stay: they are the corpus's statements, not data entry. The reset is recorded in
        the service log with your name and these figures.
      </p>

      <label class="col">
        Reason (goes in the service log)
        <input type="text" bind:value={reason} placeholder="re-boxing after straightening" />
      </label>

      <div class="row wrap">
        <button class="danger" onclick={() => void confirm()} disabled={resetting}>
          {resetting ? 'Resetting…' : `Yes, reset ${preview.siglum_raw}`}
        </button>
        <button class="subtle" onclick={cancel} disabled={resetting}>Cancel</button>
      </div>
    {/if}
  </div>
{/if}

<style>
  .reset-open {
    flex: none;
  }

  /* Full width of the header, under the row the button is in: a confirmation squeezed beside
     an `<h1>` is a confirmation nobody reads. */
  .reset-confirm,
  .reset-note {
    flex-basis: 100%;
    gap: 6px;
  }

  .what {
    margin: 0;
    padding-left: 18px;
  }

  .reset-confirm p,
  .reset-note p {
    margin: 0;
  }
</style>
